/**
 * The conversation list. INBOX scope item 7, the rendering half.
 *
 * ⛔ **IT RENDERS ROWS IT IS GIVEN AND FETCHES NOTHING ITSELF.** The data comes
 * through `subscribe()` in `web/src/data/query-store.ts`, which is the registry
 * a reconnect refetches. A component calling `fetch` directly never enters that
 * store, is never refetched, and goes stale only after a reconnect nobody
 * watched - the missed-event failure arriving through the fix for missed events.
 * `tests/web-query-registry.test.ts` scans for exactly that.
 *
 * ── ⛔ THE THREE EMPTY STATES, EACH DECIDED RATHER THAN DEFAULTED ────────────
 *
 *   loading   a skeleton is deliberately NOT rendered. The cold-load target is
 *             "the list rendered and SCROLLABLE", and a skeleton is the thing
 *             that makes a slow list feel answered while it is not. Nothing is
 *             shown until there is something real.
 *   empty     "No conversations" - a real, distinguishable statement. ⚠️ It is
 *             NOT the same as loading, and conflating them is how an agent
 *             reads a broken fetch as an empty inbox.
 *   error     the message, plus the fact that it is an error. Silence here
 *             would be indistinguishable from an empty inbox, which is the
 *             failure question 4 exists to prevent one layer up.
 */
import { useEffect, useRef } from 'react'
import { ConversationListItem } from './ConversationListItem.js'
import { markListPainted, markListRendered } from './list-rendered-mark.js'
import type { ConversationListRow } from '../../../src/services/conversation-list.js'

export type ConversationListProps = {
  /** `null` means not loaded yet, which is NOT the same as an empty array. */
  rows: ConversationListRow[] | null
  now: Date
  error?: string | null
  /**
   * The conversation currently open, so the list can say which one it is.
   *
   * ⚠️ `null` IS A REAL STATE, not a missing value: nothing is open, which is
   * what an agent sees before their first click and after a 401 recovery.
   */
  selectedId?: string | null
  onOpen?: (id: string) => void
  /**
   * Whether more history exists, and whether we are allowed to ask for it.
   * `useConversations` owns the distinction; this only renders it.
   */
  more?: 'available' | 'loading' | 'end' | 'capped'
  onLoadMore?: () => void
}

export function ConversationList({
  rows,
  now,
  error = null,
  selectedId = null,
  onOpen,
  more = 'end',
  onLoadMore,
}: ConversationListProps): React.JSX.Element {
  const listRef = useRef<HTMLUListElement | null>(null)
  const moreRef = useRef<HTMLButtonElement | null>(null)
  const hasRows = rows !== null && rows.length > 0

  /**
   * ⛔ THE SCROLL TRIGGER, AND IT IS ALLOWED TO BE ABSENT. jsdom implements no
   * `IntersectionObserver`, and neither do some older browsers - so this is
   * guarded rather than assumed. **The button works without it**, which is why
   * the observer can be a convenience instead of the mechanism.
   *
   * ⚠️ `more` IS IN THE DEPENDENCIES so the observer is rebuilt when the button
   * appears or disappears. Observing a node that React has since replaced is how
   * an auto-loader silently stops after the first page.
   */
  useEffect(() => {
    const node = moreRef.current
    if (node === null || onLoadMore === undefined) return
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((seen) => {
      if (seen.some((e) => e.isIntersecting)) onLoadMore()
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [more, onLoadMore])

  /**
   * ⛔ HOOKS RUN UNCONDITIONALLY, ABOVE EVERY EARLY RETURN. The states below
   * return before the `<ul>` exists, so a hook placed after them would be called
   * on some renders and not others - which React forbids and which would fail
   * only on the transition between states, the exact path this measures.
   *
   * The effect fires after commit; `markListPainted` then waits two animation
   * frames so the mark lands after the browser has actually painted. See
   * `list-rendered-mark.ts` for why one frame is not enough.
   */
  useEffect(() => {
    if (!hasRows) return
    markListPainted(listRef.current)
  }, [hasRows])

  if (error !== null) {
    return (
      <div className="conv-list__state" role="alert">
        Could not load conversations.
      </div>
    )
  }

  // ⛔ `null` AND `[]` ARE DIFFERENT AND MUST RENDER DIFFERENTLY. Treating them
  // alike makes "the request failed and we have nothing" look exactly like
  // "there is nothing", which is a wrong answer delivered confidently.
  if (rows === null) {
    return <div className="conv-list__state" data-testid="conv-list-loading" />
  }

  if (rows.length === 0) {
    return (
      <div className="conv-list__state" data-testid="conv-list-empty">
        No conversations
      </div>
    )
  }

  // ⛔ THE COLD-LOAD TARGET'S ONLY HONEST INSTRUMENT. `first-contentful-paint`
  // fires on the shell above, 134 ms BEFORE the API even returns, so reporting
  // it as "the list rendered" is wrong in the optimistic direction and looks
  // like a result. See `list-rendered-mark.ts`.
  // ⛔ POSITION IS THE WHOLE GUARANTEE. Every other state - loading, empty,
  // error - returns above this line, so reaching it IS the proof that rows are
  // about to be on screen. Moving this call any earlier reproduces the FCP
  // defect exactly, and three tests fail if it moves.
  markListRendered()

  return (
    <ul className="conv-list" data-testid="conv-list" ref={listRef}>
      {rows.map((row) => (
        <ConversationListItem
          key={row.id}
          row={row}
          now={now}
          selected={row.id === selectedId}
          onOpen={onOpen}
        />
      ))}

      {/*
        ⛔ A REAL BUTTON THAT ALSO AUTO-FIRES, RATHER THAN A BARE SENTINEL DIV.
        Infinite scroll alone leaves an agent with no way to continue when the
        observer does not fire - a short viewport, a browser that never
        intersects it, or reduced-motion scrolling - and no way to SEE that more
        exists. The button is the behaviour; the observer only saves the click.

        ⚠️ AND IT IS INSIDE THE `<ul>` AS AN `<li>`. A `<ul>` may only contain
        `<li>`, and a `<div>` here is invalid markup that screen readers are
        entitled to skip - which would hide the only control that continues the
        list from exactly the agent least able to scroll to it.
      */}
      {more !== 'end' && (
        <li className="conv-list__more">
          {more === 'capped' ? (
            /*
              ⛔ NOT "no more conversations". WE stopped asking; the server did
              not say the list ended. Saying otherwise would report our own
              ceiling as the shape of the data.
            */
            <p className="conv-list__more-note" data-testid="conv-list-capped">
              Showing the most recent {rows.length}. Use search to reach older conversations.
            </p>
          ) : (
            <button
              type="button"
              ref={moreRef}
              className="conv-list__more-btn"
              data-testid="conv-list-more"
              disabled={more === 'loading'}
              onClick={onLoadMore}
            >
              {more === 'loading' ? 'Loading' : 'Load Older Conversations'}
            </button>
          )}
        </li>
      )}
    </ul>
  )
}
